import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "parlenote:pwa-dismissed-at";
const REMIND_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

export function usePwaInstall(authenticated: boolean) {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent>();
  const [installed, setInstalled] = useState(
    () =>
      window.matchMedia("(display-mode: standalone)").matches ||
      // Older iOS Safari never matches the media query once installed.
      (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const isiOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const mobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(undefined);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Read once at mount rather than during render: `Date.now()` in a render pass
  // is impure and can change the answer between two renders of the same state.
  const [recentlyDismissed] = useState(() => {
    const timestamp = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    return timestamp > Date.now() - REMIND_AFTER_MS;
  });

  const visible =
    authenticated &&
    mobile &&
    !installed &&
    !recentlyDismissed &&
    !dismissedThisSession &&
    Boolean(promptEvent || isiOS);

  const install = useCallback(async () => {
    if (!promptEvent) return false;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === "accepted") {
      setPromptEvent(undefined);
      return true;
    }
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setDismissedThisSession(true);
    return false;
  }, [promptEvent]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setDismissedThisSession(true);
  }, []);

  return { visible, isiOS, canPrompt: Boolean(promptEvent), install, dismiss };
}
